/**
 * usePracticeFSM.ts — Extracted from PracticeView.vue Phase 1
 *
 * Contains the 27-state FSM that drives the practice session, plus supporting
 * enums, constants, and functions. This is a purely mechanical extraction with
 * zero logic changes — all state transitions are identical to the original.
 *
 * State remains in PracticeView's reactive object; this composable operates
 * on Ref-wrapped state properties passed via the deps interface.
 */
import type { SentenceType } from 'types/types'
import { ComputedRef, nextTick, Ref } from 'vue'
import { VueLogger } from 'vue-logger-plugin'

import { LAST_WORD_AUTO_ADVANCE_DELAY, LAST_WORD_DELAY, REPEATS } from '@/constants/practiceConstants'
import type { PitchShiftType, SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import type { RecorderService } from '@/util/RecorderService'
import { playBackStyle, userSpeakingStyle, waitingStyle } from '@/util/TextStyles'

// {{{ enums
export enum ReadOrder {
  STUDENT_ONLY,
  STUDENT_TEACHER,
  TEACHER_STUDENT,
  TEACHER_ONLY,
}

/** In TEACHER_ONLY mode the student never sings, so recording is meaningless. */
export const studentParticipates = (readOrder: ReadOrder): boolean => readOrder !== ReadOrder.TEACHER_ONLY

export enum PlayPhase {
  FIRST,
  SECOND,
}

/** Jump back is disabled when actually recording (not just practicing), during background sampling, or at the very start. */
export const isJumpBackDisabled = (recordMe: boolean, backgroundSampling: boolean, segmentIndex: number, playPhase: PlayPhase): boolean =>
  recordMe || backgroundSampling || (segmentIndex === 0 && playPhase === PlayPhase.FIRST)

// }}}

// {{{ interface PracticeFSMDeps
export interface PracticeFSMDeps {
  // State refs (from PracticeView's toRefs — FSM reads and writes these)
  fsmState: Ref<number>
  fsmHistory: Ref<string[]>
  playPhase: Ref<PlayPhase>
  playCounter: Ref<number>
  waitForVoiceStart: Ref<boolean>
  waitForVoiceEnd: Ref<boolean>
  inRepeatMode: Ref<boolean>
  requiredRepeats: Ref<number>
  segmentClicked: Ref<number | null>
  jumpBackPressed: Ref<boolean>
  manualForwardKeyPressed: Ref<boolean>
  finalizerHandler: Ref<NodeJS.Timeout | null>
  popoverShowStartNow: Ref<boolean>
  popoverVisible: Ref<boolean>
  popoverSentence: Ref<unknown | null>
  popoverSegment: Ref<unknown | null>
  popoverSentenceIndex: Ref<number | null>
  popoverSegmentIndex: Ref<number | null>
  readOrder: Ref<ReadOrder>
  manualSegmentJump: Ref<boolean>
  autoAdvance: Ref<boolean>
  focusSentenceIndex: Ref<number | null>
  focusSentenceRecordingMetadataWithPitchedAudios: Ref<SentenceRecordingMetadataWithPitchedAudios | null>
  pitchShift: Ref<PitchShiftType>
  recorderService: Ref<Pick<RecorderService, 'pause' | 'reset' | 'unpause'> | null>

  // From useFollower
  currentSegmentIndex: Ref<number>
  currentSentenceIndex: Ref<number>
  currentStyle: Ref<string>
  setCurrentSegment: (sentenceIdx: number, segmentIdx: number) => void

  // From useAudioPlayer
  setLoadPlay: (payload: {
    pitchShift: PitchShiftType
    segmentIndex: number
    sentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios
  }) => Promise<void>
  playing: Ref<boolean>
  pause: () => void

  // Computed refs (from PracticeView)
  sentence: ComputedRef<SentenceType | null>
  isSegmented: ComputedRef<boolean>
  isRecording: ComputedRef<boolean>
  recordingsInStyle: ComputedRef<Record<string, SentenceRecordingMetadataWithPitchedAudios[]>>

  // Callbacks (defined in PracticeView, hoisted functions)
  advanceToSentence: (increment: number, absolute?: boolean) => Promise<void>
  hasRecordingBySentenceIndex: (idx: number) => boolean
  pushFocusPoint: () => void
  pushSegmentPoint: (segmentIndex: number) => void
}
// }}}

export function usePracticeFSM(logger: VueLogger, deps: PracticeFSMDeps) {
  // Late-bound callback for toggleRecord (set after usePracticeSession is created)
  let _toggleRecord: () => void = () => {
    logger.error('toggleRecord called before setToggleRecord wired')
  }

  /**
   * Called by PracticeView after usePracticeSession is created, to wire the circular dependency.
   */
  function setToggleRecord(fn: () => void) {
    _toggleRecord = fn
  }

  // {{{ DRY helpers for repeated FSM patterns
  function closeRepeatModePopover(): void {
    if (deps.inRepeatMode.value) {
      deps.inRepeatMode.value = false
      deps.popoverVisible.value = false
    }
  }

  function clearFinalizer(): void {
    if (deps.finalizerHandler.value) {
      clearTimeout(deps.finalizerHandler.value)
      deps.finalizerHandler.value = null
    }
  }
  // }}}

  // {{{ initializeRecordingState
  /**
   * @precondition currentSentenceIndex is valid
   * @postcondition FSM reset to state 0; all wait flags cleared; popover closed; segment tracking reset
   * @sideeffect Resets fsmState, jumpBackPressed, waitForVoiceEnd, waitForVoiceStart, segmentClicked,
   *   popover state, inRepeatMode; calls setCurrentSegment to reset segment to -1
   */
  function initializeRecordingState() {
    deps.setCurrentSegment(deps.currentSentenceIndex.value, -1)
    deps.fsmState.value = 0
    deps.jumpBackPressed.value = false
    deps.waitForVoiceEnd.value = false
    deps.waitForVoiceStart.value = false
    deps.segmentClicked.value = null
    deps.popoverSentence.value = null
    deps.popoverSegment.value = null
    deps.popoverSentenceIndex.value = null
    deps.popoverSegmentIndex.value = null
    if (deps.inRepeatMode.value) {
      deps.inRepeatMode.value = false
    }
    deps.popoverVisible.value = false
  }
  // }}}

  // {{{ playSegmentAndSetState
  /**
   * @precondition recorderService and sentence.value are not null; FSM is in state 3 or 13
   * @postcondition Audio segment plays; on completion, FSM advances to nextFsmState IF no concurrent
   *   call has been made (race condition guard via playCounter)
   * @invariant Only the most recent call to playSegmentAndSetState will drive the FSM forward.
   *   Earlier calls that complete later are silently discarded via playCounter comparison.
   * @sideeffect Increments playCounter; pauses VAD (recorderService.pause); sets currentStyle to playBackStyle;
   *   calls setLoadPlay; on .then() sets fsmState and calls driveState
   * @async-note Returns void but initiates async audio playback. The .then() callback checks
   *   currentPlayCounter === playCounter AND currentFsmState === fsmState
   *   before advancing, preventing race conditions from rapid segment clicks.
   */
  function playSegmentAndSetState(nextFsmState: number) {
    deps.playCounter.value += 1
    const currentPlayCounter = deps.playCounter.value
    const currentFsmState = deps.fsmState.value

    console.log(
      `[FSM.playSegmentAndSetState] fsmState=${currentFsmState} -> next=${nextFsmState}, segmentIndex=${deps.currentSegmentIndex.value}, playCounter=${currentPlayCounter}`,
    )

    if (!deps.recorderService.value) {
      console.log('[FSM.playSegmentAndSetState] null recorderService, aborting')
      return
    }

    if (!deps.sentence.value) {
      console.log('[FSM.playSegmentAndSetState] null sentence.value, aborting')
      return
    }

    deps.fsmHistory.value.push('VAD-STOP')
    if (studentParticipates(deps.readOrder.value)) {
      console.log('[FSM.playSegmentAndSetState] pausing recorderService (VAD)')
      deps.recorderService.value.pause()
    }
    const sentenceRecordings = deps.recordingsInStyle.value[deps.sentence.value.id]
    if (!sentenceRecordings?.length) {
      console.log('[FSM.playSegmentAndSetState] no recordings in style for sentence', deps.sentence.value.id)
      return
    }

    deps.currentStyle.value = playBackStyle
    console.log('[FSM.playSegmentAndSetState] calling setLoadPlay')
    deps
      .setLoadPlay({
        pitchShift: deps.pitchShift.value,
        segmentIndex: deps.currentSegmentIndex.value,
        sentenceRecordingMetadataWithPitchedAudios: sentenceRecordings[0],
      })
      .then(() => {
        const makeJump = currentPlayCounter === deps.playCounter.value && currentFsmState === deps.fsmState.value
        console.log(
          `[FSM.playSegmentAndSetState] .then(): makeJump=${makeJump}, fsmState ${currentFsmState} -> ${nextFsmState} @ current=${deps.fsmState.value}`,
        )
        deps.fsmHistory.value.push(`CB [${currentFsmState} -> ${nextFsmState} @ ${deps.fsmState.value}: ${makeJump}]`)
        if (!makeJump) {
          return
        }

        deps.fsmState.value = nextFsmState
        driveState()
      })
      .catch((err: Error) => {
        console.error(`[FSM.playSegmentAndSetState] .catch(): ${err?.message}`, err?.stack)
        return
      })
  }
  // }}}

  // {{{ handleJumpBack
  /**
   * @precondition isRecording is true; FSM is in an active state
   * @postcondition FSM enters state 8 (repeat mode); VAD reset to require fresh voice trigger;
   *   popover closed; wait flags cleared to prevent async callback interference
   * @sideeffect Pauses audio; resets recorderService VAD; sets jumpBackPressed=true;
   *   clears waitForVoiceEnd/waitForVoiceStart; drives FSM to state 8
   */
  function handleJumpBack(): void {
    // stop the audio that's playing as part of practicing
    logger.debug('handleJumpBack')
    deps.fsmHistory.value.push('J')
    if (deps.playing.value) {
      deps.pause()
    }

    // we may have pressed the jump back button while voice was being detected.
    // to avoid an immediate voice detection in the state machine we now reset
    // the VAD so that the user actively needs to make sound again to trigger
    // the VAD
    if (deps.recorderService.value) {
      deps.recorderService.value.reset()
    }

    // this prevents asynchronous callbacks from messing with our FSM
    deps.popoverVisible.value = false
    deps.waitForVoiceEnd.value = false
    deps.waitForVoiceStart.value = false

    deps.jumpBackPressed.value = true
    deps.fsmState.value = 8
    driveState()
  }
  // }}}

  // {{{ driveState
  /**
   * 27-state FSM driving the practice session. See docs/Practice FSM.png
   * @precondition Either isRecording is true OR readOrder is TEACHER_ONLY (which doesn't use recorder)
   * @invariant Each state transition is atomic: sets fsmState then either recurses or exits.
   *   Async transitions (states 3, 13, 25) use callbacks/timeouts to resume.
   * @sideeffect Mutates fsmState, currentSegmentIndex (via setCurrentSegment), currentStyle,
   *   popover state, requiredRepeats, playPhase, wait flags
   * @sideeffect States 3/13: plays audio via playSegmentAndSetState (pauses VAD during playback)
   * @sideeffect State 5: unpauses VAD via recorderService.unpause()
   * @sideeffect State 20: either auto-advances to next sentence or stops recording
   * @sideeffect State 25: sets finalizerHandler timeout for STUDENT_TEACHER end-of-sentence delay
   * @async-note playSegmentAndSetState (states 3, 13) returns Promise; FSM resumes in .then()
   *   State 25 uses setTimeout with LAST_WORD_DELAY or LAST_WORD_AUTO_ADVANCE_DELAY
   */
  function driveState() {
    console.log(`[FSM.driveState] state=${deps.fsmState.value}`)
    deps.fsmHistory.value.push(deps.fsmState.value.toString())
    if (studentParticipates(deps.readOrder.value) && !deps.isRecording.value) {
      logger.error('abort driveState, not recording')
      return
    }

    switch (deps.fsmState.value) {
      case 0:
        deps.setCurrentSegment(deps.currentSentenceIndex.value, deps.currentSegmentIndex.value + 1)
        deps.pushSegmentPoint(deps.currentSegmentIndex.value)
        closeRepeatModePopover()
        deps.requiredRepeats.value = 1
        deps.fsmState.value = 1
        driveState()
        break

      case 1:
        deps.currentStyle.value = waitingStyle
        deps.playPhase.value = PlayPhase.FIRST
        deps.fsmState.value = 2
        driveState()
        break

      case 2:
        if (!deps.sentence.value) {
          logger.error('unexpected null sentence.value')
          break
        }

        if (
          deps.readOrder.value === ReadOrder.TEACHER_STUDENT ||
          deps.readOrder.value === ReadOrder.TEACHER_ONLY ||
          deps.sentence.value.isResponsive
        ) {
          deps.fsmState.value = 3
        } else {
          deps.fsmState.value = 4
        }
        driveState()
        break

      case 3:
        // set currentStyle to playBackStyle
        playSegmentAndSetState(4)
        break

      case 4:
        if (deps.segmentClicked.value) {
          deps.fsmState.value = 7
        } else {
          deps.fsmState.value = 22
        }
        driveState()
        break

      case 5:
        if (deps.jumpBackPressed.value) {
          deps.fsmState.value = 8
          deps.jumpBackPressed.value = false
          driveState()
        } else {
          // even though user hasn't spoken yet, color change indicates that system is waiting
          deps.currentStyle.value = waitingStyle
          deps.waitForVoiceStart.value = true
          if (!deps.recorderService.value) {
            logger.error('unexpected null recorderService')
            break
          } else if (studentParticipates(deps.readOrder.value)) {
            deps.fsmHistory.value.push('VAD-START-5')
            deps.recorderService.value.unpause()
          }
        }
        break

      // invoked by onVoiceStart()
      case 6:
        deps.waitForVoiceStart.value = false
        deps.fsmState.value = 9
        driveState()
        break

      case 7:
        clearFinalizer()
        deps.requiredRepeats.value = 1
        closeRepeatModePopover()

        if (deps.segmentClicked.value === null) {
          logger.error('unexpected null segmentClicked')
          break
        }
        deps.setCurrentSegment(deps.currentSentenceIndex.value, deps.segmentClicked.value)
        deps.pushSegmentPoint(deps.segmentClicked.value)
        deps.segmentClicked.value = null
        deps.fsmState.value = 1
        driveState()
        break

      case 8:
        clearFinalizer()
        closeRepeatModePopover()

        deps.requiredRepeats.value = REPEATS
        deps.inRepeatMode.value = true
        deps.jumpBackPressed.value = false
        nextTick(() => {
          deps.popoverVisible.value = true
        })

        if (deps.playPhase.value === PlayPhase.FIRST) {
          deps.fsmState.value = 26
        } else if (deps.playPhase.value === PlayPhase.SECOND) {
          deps.fsmState.value = 1
        } else {
          logger.error(`unexpected playPhase ${deps.playPhase.value}`)
        }
        driveState()
        break

      // See Practice.vue for documentation
      // the countdown flag [3, 2, 1, ...]
      case 9:
        deps.currentStyle.value = userSpeakingStyle
        deps.waitForVoiceEnd.value = true

        if (deps.readOrder.value === ReadOrder.TEACHER_STUDENT) {
          deps.playPhase.value = PlayPhase.SECOND
        }
        // now wait for onVoiceStop();
        break

      // invoked by onVoiceStop()
      case 10:
        deps.waitForVoiceEnd.value = false
        deps.fsmState.value = 23
        driveState()
        break

      case 11:
        if (deps.readOrder.value === ReadOrder.TEACHER_STUDENT) {
          deps.fsmState.value = 12
        } else {
          deps.fsmState.value = 13
        }
        driveState()
        break

      case 12:
        if (deps.segmentClicked.value) {
          deps.fsmState.value = 7
        } else {
          deps.fsmState.value = 14
        }
        driveState()
        break

      case 13:
        playSegmentAndSetState(12)
        deps.playPhase.value = PlayPhase.SECOND
        break

      case 14:
        if (deps.jumpBackPressed.value) {
          deps.fsmState.value = 8
          deps.jumpBackPressed.value = false
        } else {
          deps.fsmState.value = 15
        }
        driveState()
        break

      case 15:
        if (deps.manualSegmentJump.value) {
          deps.fsmState.value = 16
          // user pressed it too early; don't wait for them to press it again
          if (deps.manualForwardKeyPressed.value) {
            driveState()
          }
          // driveState() invoked by key press handler
        } else {
          deps.fsmState.value = 17
          driveState()
        }
        break

      case 16:
        // invoked by keydown event listener
        if (deps.manualForwardKeyPressed.value) {
          deps.manualForwardKeyPressed.value = false
          deps.fsmState.value = 17
          driveState()
        }
        break

      case 17:
        deps.requiredRepeats.value -= 1
        deps.fsmState.value = 18
        driveState()
        break

      case 18:
        if (deps.requiredRepeats.value > 0) {
          deps.fsmState.value = 1
        } else {
          deps.fsmState.value = 19
        }
        driveState()
        break

      case 19:
        if (!deps.focusSentenceRecordingMetadataWithPitchedAudios.value) {
          logger.error('unexpected null focusSentenceRecordingMetadataWithPitchedAudios')
          break
        }
        if (!deps.focusSentenceRecordingMetadataWithPitchedAudios.value.sentenceRecording) {
          logger.error('unexpected null focusSentenceRecordingMetadataWithPitchedAudios.sentenceRecording')
          break
        }

        if (
          deps.currentSegmentIndex.value + 1 >=
          deps.focusSentenceRecordingMetadataWithPitchedAudios.value.sentenceRecording.segments.length
        ) {
          deps.fsmState.value = 24
        } else {
          deps.fsmState.value = 0
        }
        driveState()
        break

      case 20:
        // move highlight away from last word.
        // note that if we came from fsmState 25, we are already past the end
        // of the sentence; it doesn't really hurt
        deps.setCurrentSegment(deps.currentSentenceIndex.value, deps.currentSegmentIndex.value + 1)

        if (deps.focusSentenceIndex.value === null) {
          logger.error('unexpected null focusSentenceIndex')
          break
        }

        // auto-advance: proceed to next sentence
        if (deps.autoAdvance.value && deps.hasRecordingBySentenceIndex(deps.focusSentenceIndex.value + 1)) {
          deps.advanceToSentence(1).then(() => {
            deps.fsmState.value = 21
            driveState()
            clearFinalizer()
          })
          break
        }

        // otherwise: this is the end
        _toggleRecord()
        break

      case 21:
        deps.setCurrentSegment(deps.currentSentenceIndex.value, -1)
        deps.fsmState.value = 0
        driveState()
        break

      case 22:
        if (!deps.sentence.value) {
          logger.error('unexpected null sentence.value')
          return
        }
        if (!studentParticipates(deps.readOrder.value) || deps.sentence.value.isResponsive) {
          deps.fsmState.value = 14
        } else {
          deps.fsmState.value = 5
        }
        driveState()
        break

      case 23:
        if (deps.readOrder.value === ReadOrder.STUDENT_ONLY) {
          deps.fsmState.value = 14
        } else {
          deps.fsmState.value = 11
        }
        driveState()
        break

      case 24:
        closeRepeatModePopover()

        if (deps.readOrder.value === ReadOrder.STUDENT_TEACHER) {
          deps.fsmState.value = 25
        } else {
          deps.fsmState.value = 20
        }
        driveState()
        break

      case 25:
        // move highlight away from last word; the user may still press on Jump Back
        deps.setCurrentSegment(deps.currentSentenceIndex.value, deps.currentSegmentIndex.value + 1)

        if (deps.focusSentenceIndex.value === null) {
          logger.error('unexpected null focusSentenceIndex')
          break
        }

        deps.finalizerHandler.value = setTimeout(
          () => {
            deps.fsmState.value = 20
            driveState()
          },
          deps.autoAdvance.value && deps.hasRecordingBySentenceIndex(deps.focusSentenceIndex.value + 1)
            ? LAST_WORD_AUTO_ADVANCE_DELAY
            : LAST_WORD_DELAY,
        )
        break

      case 26:
        deps.setCurrentSegment(deps.currentSentenceIndex.value, Math.max(0, deps.currentSegmentIndex.value - 1))
        deps.fsmState.value = 1
        driveState()
        break

      default:
        break
    }
  }
  // }}}

  return {
    driveState,
    handleJumpBack,
    initializeRecordingState,
    playSegmentAndSetState,
    setToggleRecord,
  }
}
